import 'LIBRARY/common';
import { numberNormalizer } from 'LIBRARY/helpers';
import { q, qq } from "bobjoll/ts/library/dom";

export class Timeline {
    private static readonly requestAnimationFrame = (
        (window as any).requestAnimationFrame ||
        (window as any).webkitRequestAnimationFrame ||
        (window as any).mozRequestAnimationFrame ||
        (window as any).msRequestAnimationFrame ||
        (window as any).oRequestAnimationFrame
    ).bind(window);
    private static position = Timeline.getPosition();

    private stepItemElements: HTMLElement[];
    private stepListElement: HTMLElement;
    private stepActiveIndex: number = 0;
    private stepAnimationActive: boolean = false;
    private timelineElement: HTMLElement;
    private scrollZone: HTMLElement;
    private touchStartX: number;
    private touchStartY: number;

    constructor(options: TimelineOptions) {
        let stepItemElements = qq('.timeline__steps li', options.element);
        let stepListElement = q('.timeline__steps', options.element);

        if (stepItemElements && stepListElement) { 
            this.stepItemElements = stepItemElements;
            this.stepListElement = stepListElement;
            this.timelineElement = options.element;
            this.scrollZone = this.timelineElement.closest('.about__timeline') as HTMLElement;
            this.eventHandlerRequestAnimation();
        }

        this.stepItemElements.forEach((item, index) => {
            item.addEventListener('click', () => {
                this.setActive(index);
                this.stepActiveIndex = index;
            });
        });
    }

    private static getPosition() {
        return window.pageYOffset || document.getPathValue('documentElement.scrollTop') || document.getPathValue('body.scrollTop');
    }

    private static getTimelinePosition(element: HTMLElement) {
        let elementBound = element.getBoundingClientRect();
        return (Timeline.getPosition() + elementBound.top) - ((window.innerHeight - elementBound.height) / 2);
    }

    private setActive(index: number) {
        const pixelOffset = 3;
        let stepItemPrevElement = this.stepItemElements[index - 1];
        let stepItemNextElement = this.stepItemElements[index + 1];
        if (this.stepItemElements[index]) {
            let elementStyle = window.getComputedStyle(this.stepItemElements[index]);
            let elementWidth =
                this.stepItemElements[index].clientWidth +
                numberNormalizer(elementStyle.marginLeft || '') +
                numberNormalizer(elementStyle.marginRight || '');
            this.stepListElement.style.transform = `translateX(-${elementWidth * index + (index * pixelOffset)}px)`;
            this.stepItemElements.forEach((step, i) => {
                step.classList[i === index ? 'add' : 'remove']('active');
                step.classList.remove('prev-step', 'next-step');
            });
        }
        if (stepItemPrevElement) {
            stepItemPrevElement.classList.add('prev-step');
        }
        if (stepItemNextElement) {
            stepItemNextElement.classList.add('next-step');
        }
    }

    private preventDefault(event: any) {
        event = event || window.event;
        if (event.preventDefault) {
            event.preventDefault();
        }
        window.scrollTo(0, Timeline.getTimelinePosition(this.timelineElement));
        event.returnValue = false;
    }

    private inRange() {
        let centralPosition = (window.innerHeight / 2);
        let timelineElementBound = this.timelineElement.getBoundingClientRect();
        return timelineElementBound.top <= centralPosition && timelineElementBound.bottom >= centralPosition;
    }

    private eventHandlerScroll(event: any, isMobile = false) {
        if (!this.isTimelineHovered() && !isMobile) return;

        this.preventDefault(event);

        if (!this.stepAnimationActive) {
            this.updateTimelineStep(event, isMobile);
            this.stepAnimationActive = true;
            this.setActive(this.stepActiveIndex);
            setTimeout(() => this.stepAnimationActive = false, 750);
        }
    }

    private eventHandlerRequestAnimation() {
        const position = Timeline.getPosition();

        if (position !== Timeline.position) {
            const timelineInRange  = this.inRange();

            this.scrollZone.onwheel = (event: any) => timelineInRange ? this.eventHandlerScroll(event) : null; // modern standard
            this.scrollZone.ontouchstart  = (event: any) => timelineInRange ? this.saveTouchStartCoordinate(event) : null; // mobile
            this.scrollZone.ontouchmove  = (event: any) => timelineInRange ? this.eventHandlerScroll(event, true) : null; // mobile
            this.scrollZone.onscroll = (event: any) => timelineInRange ? this.eventHandlerScroll(event) : null;

            Timeline.position = Timeline.getPosition();
        }

        Timeline.requestAnimationFrame(this.eventHandlerRequestAnimation.bind(this));
    }

    private isTimelineHovered() {
        return this.scrollZone.parentElement?.querySelector(':hover') === this.scrollZone;
    }

    private updateTimelineStep(event: any, isMobile: boolean) {
        let moveToNextStep = false;

        if (isMobile) {
            const currentX = event.touches[0].clientX;
            const currentY = event.touches[0].clientY;

            const diffX = this.touchStartX - currentX;
            const diffY = this.touchStartY - currentY;

            const slideHorizontally = Math.abs(diffX) > Math.abs(diffY);

            if (slideHorizontally) {
                moveToNextStep = diffX > 0;
            }
        } else {
            moveToNextStep = event.deltaY > 0;
        }

        if (moveToNextStep && this.stepActiveIndex < this.stepItemElements.length - 1) {
            this.stepActiveIndex++;
        }

        if (!moveToNextStep && this.stepActiveIndex > 0) {
            this.stepActiveIndex--;
        }
    }

    private saveTouchStartCoordinate(event: any) {
        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
    }
}

interface TimelineOptions {
    element: HTMLElement;
}
